import React, { useState, useEffect, useCallback } from 'react';
import { Col, Row, Modal, DatePicker, Spin, Select, Button, Card, Typography, Pagination, Dropdown, Menu, message } from 'antd';
import Box from '@iso/components/utility/box';
import LayoutWrapper from "@iso/components/utility/layoutWrapper.js";
import SensorsPage from "./Sensors.styles";
import moment from 'moment-timezone';
import { BellOutlined, EditOutlined, SettingOutlined, EnvironmentOutlined, PlusOutlined, EyeOutlined, DeleteOutlined, StarOutlined, StarFilled } from '@ant-design/icons';
import { debounce } from 'lodash';
import { sensorService } from '../../../services/sensor.service';
import { deviceSensorService } from '../../../services/deviceSensor.service';
import { sensorViewService } from '../../../services/sensorView.service';
import IntlMessages from '@iso/components/utility/intlMessages';
import EditSensorNameModal from './EditSensorNameModal';
import AddDeviceModal from './AddDeviceModal';
import SensorDetail from './SensorDetail';
import TVDisplayMode from './TVDisplayMode';
import { useSelector } from 'react-redux';
import { commonHelper } from '@iso/lib/helpers/commonHelper';
import { userService, corporateLoraDeviceService } from '@iso/services';
import { sensorTypeSettingService } from '../../../services/sensorTypeSetting.service';
import { Input } from 'antd';
import { InputSearch } from '../../../components/uielements/input';
import { Map, TileLayer, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import StorageTankDisplay from './StorageTankDisplay';
import {
  TemperatureDisplay,
  HumidityDisplay,
  MoistureDisplay,
  PressureDisplay,
  VoltageDisplay,
  ConductivityDisplay,
  RelayDisplay,
  DistanceDisplay,
  HeightDisplay,
  WindSpeedDisplay,
  WindDirectionDisplay,
  VentPositionDisplay,
  LightDisplay,
  DoorStatusDisplay,
  MeterReadingDisplay,
  WaterMeterDisplay,
  GenericGaugeDisplay
} from './SensorVisualisations';
import { getSensorTypeLabel } from './doorSensorUtils';
import { SENSOR_TYPE_DOOR_CONTACT_SENSOR_WATER } from '@iso/constants/trackpacSensorTypes';
import {
  getLoraDeviceLabel,
  humanizeSensorTypeName,
  looksLikeDevEui,
  MODEL_CODE_BY_SENSOR_TYPE,
  SENSOR_TYPE_AI_METER_READER,
} from '@iso/constants/trackpacSensorTypes';
import {
  Temperature,
  Humidity,
  Pressure,
  Distance,
  AirQuality,
  Light,
  UV,
  Weather,
  Visibility,
  Electricity,
  Vibration,
  Relay,
  DefaultSensor
} from './sensorIcons'

const { Search } = Input;
const { Option } = Select; 
const { RangePicker } = DatePicker;
const { Title, Text } = Typography;

const dateFormat = 'DD/MM/YYYY HH:mm:ss';
const defaultTimelineOption = 'last_24_hours';

const TvIcon = ({ style = {} }) => (
  <svg 
    viewBox="0 0 24 24" 
    fill="currentColor" 
    style={{ width: '1em', height: '1em', ...style }}
  >
    <path d="M21 3H3c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h5v2h8v-2h5c1.1 0 1.99-.9 1.99-2L23 5c0-1.1-.9-2-2-2zm0 14H3V5h18v12z"/>
  </svg>
);

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

const createAssetIcon = (speed = 0) => {
  const speedText = Math.round(speed).toString();
  const svgContent = `
    <svg width="75" height="87.5" viewBox="0 0 300 350" xmlns="http://www.w3.org/2000/svg">
      <path d="M225 137.5 C225 89.2 185.8 50 137.5 50 S50 89.2 50 137.5 c0 48.3 87.5 192.5 87.5 192.5 s87.5-144.2 87.5-192.5 z" fill="#0d3825"/>
      <circle cx="137.5" cy="137.5" r="58.3" fill="white" stroke="white" stroke-width="14"/>
      <circle cx="137.5" cy="137.5" r="42" fill="white"/>
      <circle cx="220" cy="80" r="30" fill="#0d3825"/>
      <text x="220" y="88" text-anchor="middle" font-family="Arial, sans-serif" font-size="24" fill="white" font-weight="bold">${speedText}</text>
    </svg>
  `;
  
  return new L.Icon({
    iconUrl: 'data:image/svg+xml;base64,' + btoa(svgContent),
    iconSize: [60, 70],
    iconAnchor: [37.5, 87.5],
    popupAnchor: [0, -87.5]
  });
};

const Sensors = (props) => {
    const activePropertyId = useSelector((state) => state.property.activePropertyId);
    const activeProperty = commonHelper.getActiveProperty();
    const propertyTimezone = activeProperty?.timezone ? activeProperty.timezone : 'UTC';

    const activePermission = useSelector((state) => state.permission.activePermission);
    const canAddDevice = activePermission === 'owner' || activePermission === 'admin';
    const [visibleAddDevice, setVisibleAddDevice] = useState(false);
    const [hasLoraLink, setHasLoraLink] = useState(false);
    const [loraDevices, setLoraDevices] = useState([]);

    const [selectedTimezone, setSelectedTimezone] = useState(propertyTimezone);
    const defaultStartDate = moment().tz(selectedTimezone).subtract(23, 'hours').subtract(59, 'minutes').format(dateFormat);
    const defaultEndDate = moment().tz(selectedTimezone).format(dateFormat);
    
    const [searchTerm, setSearchTerm] = useState('');
    const [sensors, setSensors] = useState([]);
    const [devices, setDevices] = useState([]);
    const [loading, setLoading] = useState(false);
    const [startDate, setStartDate] = useState(defaultStartDate);
    const [endDate, setEndDate] = useState(defaultEndDate);
    const [editSensorNameModalVisible, setEditSensorNameModalVisible] = useState(false);
    const [editSensor, setEditSensor] = useState({});
    const [statsSensor, setStatsSensor] = useState({});
    const [typeModalSensor, setTypeModalSensor] = useState('edit');
    const [sensorType, setSensorType] = useState('temperature');
    const [usersProperty, setUsersProperty] = useState([]);
    const [searchValue, setSearchValue] = useState('');
    
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [totalItems, setTotalItems] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    
    const [currentView, setCurrentView] = useState('list');
    const [selectedSensor, setSelectedSensor] = useState(null);
    const [sensorSettings, setSensorSettings] = useState({});
    const [editDevice, setEditDevice] = useState({});
    const [editDeviceField, setEditDeviceField] = useState('');

    // Sensor Views state
    const [sensorViews, setSensorViews] = useState([]);
    const [selectedViewId, setSelectedViewId] = useState(null);
    const [viewsLoading, setViewsLoading] = useState(false);
    const [createViewModalVisible, setCreateViewModalVisible] = useState(false);
    const [newViewName, setNewViewName] = useState('');
    const [newViewDescription, setNewViewDescription] = useState('');
    const [sensorViewMap, setSensorViewMap] = useState({})
    useEffect(() => {
        if (activePropertyId) {
            loadSensorViews();
        }
    }, [activePropertyId]);

    useEffect(() => {
    if (sensorViews.length > 0) {
        const map = {};
        sensorViews.forEach(view => {
            if (view.sensor_ids && Array.isArray(view.sensor_ids)) {
                view.sensor_ids.forEach(sensorId => {
                    if (!map[sensorId]) {
                        map[sensorId] = [];
                    }
                    map[sensorId].push(view.id);
                });
            }
        });
        setSensorViewMap(map);
        }
    }, [sensorViews]);

    const loadSensorViews = async () => {
        setViewsLoading(true);
        try {
            const res = await sensorViewService.getList({ 
                property_id: activePropertyId,
                pageSize: 100 
            });
            if (res && res.code === '0000') {
                setSensorViews(res.views || []);
            }
        } catch (error) {
            console.error('Error loading sensor views:', error);
        } finally {
            setViewsLoading(false);
        }
    };

    const loadSensorTypeSettings = async (sensorId) => {
        if (sensorSettings[sensorId] !== undefined) {
            return sensorSettings[sensorId];
        }
        
        try {
            const res = await sensorTypeSettingService.getList(sensorId);
            if (res.code === "0000") {
                setSensorSettings(prev => ({
                    ...prev,
                    [sensorId]: res.sensor_type_settings || []
                }));
                return res.sensor_type_settings || [];
            }
            return [];
        } catch (error) {
            console.warn("Unable to retrieve sensorTypeSettings", error);
            return [];
        }
    };

    const hasSensorTypeSettings = (sensorId, sensorType) => {
        const settings = sensorSettings[sensorId];
        if (!settings || !Array.isArray(settings)) {
            return false;
        }
        return settings.some(setting => 
            setting.sensor_id === sensorId && setting.type === sensorType
        );
    };

    useEffect(() => {
        if (sensors.length > 0) {
            sensors.forEach(sensor => {
                loadSensorTypeSettings(sensor.id);
            });
        }
    }, [sensors]);

    useEffect(() => {
        if (activePropertyId) {
            // Clear devices before loading new data
            setDevices([]);
            setLoading(true);
            
            if (selectedViewId) {
                loadViewData(selectedViewId);
            } else {
                getDevices(activePropertyId, startDate, endDate, selectedTimezone);
            }
            getUsers(activePropertyId);
        }
    }, [activePropertyId, startDate, endDate, currentPage, pageSize, selectedViewId]);

    // Decide whether to show the "Add device" button: only for property
    // owners/admins whose corporate group is linked to a LoRa organisation.
    // The backend short-circuits (no LoRa call) when there is no link.
    useEffect(() => {
        if (activePropertyId && canAddDevice) {
            corporateLoraDeviceService.getDevices(activePropertyId)
                .then((res) => {
                    if (res && res.code === '0000' && res.linked) {
                        setHasLoraLink(true);
                        setLoraDevices(res.data || []);
                    } else {
                        setHasLoraLink(false);
                        setLoraDevices([]);
                    }
                })
                .catch(() => {
                    setHasLoraLink(false);
                    setLoraDevices([]);
                });
        } else {
            setHasLoraLink(false);
        }
    }, [activePropertyId, canAddDevice]);
    
    useEffect(() => {
        if (devices.length > 0) {
            setSensors(devices.flatMap(device => device.sensors));
        }
    }, [devices]);

    const loadViewData = async (viewId) => {
        setLoading(true);
        setDevices([]); // Clear devices immediately when starting to load
        try {
            const res = await sensorViewService.getDetail(viewId, {
                start_date: startDate,
                end_date: endDate,
                timezone: selectedTimezone,
                is_show_sensors: true
            });
            
            if (res && res.code === '0000') {
                // The API now returns devices directly, not sensors
                const devicesWithData = res.devices || [];
                
                setDevices(devicesWithData);
                setTotalItems(res.total_item || 0);
                setTotalPages(res.total_page || 1);
            } else {
                // If response code is not success, clear devices
                setDevices([]);
                setTotalItems(0);
                setTotalPages(0);
            }
        } catch (error) {
            console.error('Error loading view data:', error);
            message.error('Failed to load sensor view');
            // Clear devices on error
            setDevices([]);
            setTotalItems(0);
            setTotalPages(0);
        } finally {
            setLoading(false);
        }
    };

    const getUsers = (propertyId) => {
        userService.getUserForProperty(propertyId).then(res => {
          if (res.code === '0000') {
            setUsersProperty(res.users);
          }
        });
    };

const isConfiguredStorageTank = (sensor) => {
    
    if (!sensor.asset) {
        return false;
    }
    
    const asset = sensor.asset;
    
    // Check if it's a storage category
    if (asset.category !== 'storage') {
        return false;
    }
    
    // Check if all required configuration fields are present and valid
    const hasValidConfig = 
        asset.height_at_100 != null &&
        asset.litres_at_100 != null &&
        asset.height_at_0 != null &&
        asset.litres_at_0 != null &&
        !isNaN(parseFloat(asset.height_at_100)) &&
        !isNaN(parseFloat(asset.litres_at_100)) &&
        !isNaN(parseFloat(asset.height_at_0)) &&
        !isNaN(parseFloat(asset.litres_at_0));
    
    if (!hasValidConfig) {
        console.log('Invalid configuration detected');
        return false;
    }
    
    // Check if sensor has distance OR height data (both used for height measurement in tanks)
    const hasDistanceOrHeightData = sensor.timelines?.some(timeline => {
        const hasDistance = timeline.distance && timeline.distance.value != null;
        const hasHeight = timeline.height && timeline.height.value != null;
        return hasDistance || hasHeight;
    });
    
    
    return hasDistanceOrHeightData;
};

    const getLastKnownValue = (sensor, sensorType) => {
        if (!sensor.timelines || !sensor.timelines.length) {
            return null;
        }
        for (let i = sensor.timelines.length - 1; i >= 0; i--) {
            const timeline = sensor.timelines[i];
            if (timeline[sensorType]) {
                return {
                    value: timeline[sensorType].value,
                    unit: timeline[sensorType].unit || '',
                    time: timeline[sensorType].time
                };
            }
        }
        return null;
    };

    const getLastKnownLocation = (sensor) => {
        if (!sensor.timelines || !sensor.timelines.length) {
            return null;
        }
        let latestLocation = null;
        let latestTimestamp = 0;
        sensor.timelines.forEach(timeline => {
            const lat = timeline.latitude?.value;
            const lng = timeline.longitude?.value;
            const speed = timeline.speed?.value;
            const time = timeline.latitude?.time || timeline.longitude?.time;
            if (lat && lng && lat !== 0 && lng !== 0 && time) {
                const timestamp = moment(time, 'DD-MM-YYYY HH:mm').valueOf();
                if (timestamp > latestTimestamp) {
                    latestTimestamp = timestamp;
                    latestLocation = { lat, lng, speed, time };
                }
            }
        });
        return latestLocation;
    };

    const getSensorTypes = (sensor) => {
        if (!sensor.timelines || !sensor.timelines.length) {
            return [];
        }
        const types = new Set();
        sensor.timelines.forEach(timeline => {
            Object.keys(timeline).forEach(key => {
                if (key !== 'time') {
                    types.add(key);
                }
            });
        });
        return Array.from(types);
    };

    const getSensorTypeIcon = (sensorType, className = "sidebar-icon") => {
        const iconMap = {
            temperature: Temperature,
            humidity: Humidity,
            moisture: Humidity,
            pressure: Pressure,
            distance: Distance,
            air_quality: AirQuality,
            light: Light,
            uv: UV,
            weather: Weather,
            visibility: Visibility,
            height: Distance,
            conductivity: Electricity,
            voltage: Electricity,
            vibration: Vibration,
            relay: Relay,
            asset_tracking: () => <EnvironmentOutlined className={className} />,
            default: DefaultSensor
        };
        const IconComponent = iconMap[sensorType.toLowerCase()] || iconMap.default;
        return <IconComponent className={className} />;
    };

    const handleSensorClick = (sensor) => {
        window.scrollTo({ top: 0, behavior: 'auto' });
        document.documentElement.scrollTop = 0;
        document.body.scrollTop = 0;
        const scrollableElement = document.querySelector('.ant-layout-content') || 
                                document.querySelector('.layout-wrapper') ||
                                document.querySelector('[data-scrollable="true"]');
        if (scrollableElement) {
            scrollableElement.scrollTop = 0;
        }
        setSelectedSensor(sensor);
        setCurrentView('detail');
    };

    const handleBackToList = () => {
        setCurrentView('list');
        setSelectedSensor(null);
    };

    const handlePageChange = (page) => {
        setCurrentPage(page);
    };

    const handlePageSizeChange = (current, size) => {
        setPageSize(size);
        setCurrentPage(1);
    };

    const handleAddToView = async (sensorId, viewId) => {
        try {
            const res = await sensorViewService.addSensors(viewId, [sensorId]);
            if (res && res.code === '0000') {
                if (res.added_count > 0) {
                    message.success(res.message || 'Sensor added to view');
                    loadSensorViews(); // Reload to update the sensor_ids in views
                } else if (res.skipped_count > 0) {
                    message.info('Sensor is already in this view');
                }
            } else {
                message.error('Failed to add sensor to view');
            }
        } catch (error) {
            console.error('Error adding sensor to view:', error);
            message.error('Failed to add sensor to view');
        }
    };

    const handleRemoveFromView = async (sensorId, viewId) => {
        try {
            const res = await sensorViewService.removeSensors(viewId, [sensorId]);
            if (res && res.code === '0000') {
                message.success('Sensor removed from view');
                loadViewData(viewId);
            } else {
                message.error('Failed to remove sensor from view');
            }
        } catch (error) {
            console.error('Error removing sensor from view:', error);
            message.error('Failed to remove sensor from view');
        }
    };

    const handleCreateView = async () => {
        if (!newViewName.trim()) {
            message.error('Please enter a view name');
            return;
        }
        try {
            const res = await sensorViewService.create({
                property_id: activePropertyId,
                name: newViewName,
                description: newViewDescription,
                is_default: 0,
                sensor_ids: []
            });
            if (res && res.code === '0000') {
                message.success('View created successfully');
                setCreateViewModalVisible(false);
                setNewViewName('');
                setNewViewDescription('');
                loadSensorViews();
            } else {
                message.error('Failed to create view');
            }
        } catch (error) {
            console.error('Error creating view:', error);
            message.error('Failed to create view');
        }
    };

    const handleDeleteView = async (viewId) => {
        Modal.confirm({
            title: 'Delete View',
            content: 'Are you sure you want to delete this view? This action cannot be undone.',
            okText: 'Delete',
            okType: 'danger',
            cancelText: 'Cancel',
            onOk: async () => {
                try {
                    const res = await sensorViewService.destroy(viewId);
                    if (res && res.code === '0000') {
                        message.success('View deleted successfully');
                        if (selectedViewId === viewId) {
                            setSelectedViewId(null);
                        }
                        loadSensorViews();
                    } else {
                        message.error('Failed to delete view');
                    }
                } catch (error) {
                    console.error('Error deleting view:', error);
                    message.error('Failed to delete view');
                }
            }
        });
    };
    // Also update the renderSensorMenu function to stop propagation on menu item clicks:

    const renderSensorMenu = (sensor) => {
        const sensorInViews = sensorViewMap[sensor.id] || [];
        
        const menuItems = [
            {
                key: 'add-to-view',
                label: 'Add to View',
                icon: <PlusOutlined />,
                children: sensorViews.map(view => {
                    const isInView = sensorInViews.includes(view.id);
                    return {
                        key: `add-${view.id}`,
                        label: (
                            <span style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                                <span>{view.name}</span>
                                {isInView && (
                                    <span style={{ marginLeft: '12px', color: '#52c41a', fontSize: '16px' }}>✓</span>
                                )}
                            </span>
                        ),
                        disabled: isInView,
                        onClick: (e) => {
                            e.domEvent.stopPropagation(); // Stop the click from bubbling to parent
                            if (!isInView) {
                                handleAddToView(sensor.id, view.id);
                            }
                        }
                    };
                })
            }
        ];

        // Show which views this sensor is in
        if (sensorInViews.length > 0 && !selectedViewId) {
            const viewNames = sensorInViews
                .map(viewId => sensorViews.find(v => v.id === viewId)?.name)
                .filter(Boolean);
            
            menuItems.unshift({
                key: 'in-views',
                label: (
                    <div style={{ padding: '4px 0', borderBottom: '1px solid #f0f0f0', marginBottom: '4px' }}>
                        <Text type="secondary" style={{ fontSize: '12px' }}>
                            In {sensorInViews.length} view(s): {viewNames.join(', ')}
                        </Text>
                    </div>
                ),
                disabled: true
            });
        }

        if (selectedViewId) {
            menuItems.push({
                key: 'remove-from-view',
                label: 'Remove from this View',
                icon: <DeleteOutlined />,
                danger: true,
                onClick: (e) => {
                    e.domEvent.stopPropagation(); // Stop the click from bubbling to parent
                    handleRemoveFromView(sensor.id, selectedViewId);
                }
            });
        }
        
        return <Menu items={menuItems} />;
    };


    const renderAssetTrackingMap = (sensor) => {
        const location = getLastKnownLocation(sensor);
        if (!location) {
            return (
                <div style={{ textAlign: 'center', padding: '20px', backgroundColor: '#f5f5f5', borderRadius: '6px', marginTop: '8px' }}>
                    <EnvironmentOutlined style={{ fontSize: '24px', color: '#ccc', marginBottom: '8px' }} />
                    <Text type="secondary" style={{ fontSize: '12px' }}>No recent location data</Text>
                </div>
            );
        }
        return (
            <div style={{ marginTop: '8px' }} onClick={(e) => e.stopPropagation()}>                
                <div style={{ height: '200px', width: '100%', borderRadius: '6px', overflow: 'hidden' }} onClick={(e) => e.stopPropagation()}>
                    <Map center={[location.lat, location.lng]} zoom={15} style={{ height: '100%', width: '100%' }} zoomControl={false} attributionControl={false}>
                        <TileLayer url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}" />
                        <Marker position={[location.lat, location.lng]} icon={createAssetIcon(location.speed)}>
                            <Popup>
                                <div>
                                    <strong>{sensor.asset?.title || sensor.name || 'Asset'}</strong><br />
                                    <strong>Last Seen:</strong> {location.time}<br />
                                    <strong>Speed:</strong> {Math.round(location.speed)} km/h<br />
                                    <strong>Coordinates:</strong> {location.lat.toFixed(6)}, {location.lng.toFixed(6)}
                                </div>
                            </Popup>
                        </Marker>
                    </Map>
                </div>
            </div>
        );
    };

const renderDeviceCards = useCallback(() => {
    if (!devices || devices.length === 0) return null;
    
    // Flatten devices into individual sensor cards
    const sensorCards = [];
    
    devices.forEach((device) => {
        const deviceId = device.terminal_id || device.id;
        const deviceInfo = {
            id: device.id,
            deviceId: deviceId,
            deviceName: device.device_name,
            deviceDescription: device.device_description,
            // Carry just enough device metadata for the card to resolve a
            // friendly model label when the sensor's own name is missing or
            // is the raw DEV EUI (the default coming from lora.airagri.tech).
            deviceMeta: {
                device_type: device.device_type,
                device_brand: device.device_brand,
                name: device.name, // upstream-supplied identifier, often the DEV EUI
            },
        };
        
        if (device.sensors && device.sensors.length > 0) {
            device.sensors.forEach((sensor, sensorIndex) => {
                sensorCards.push({
                    ...deviceInfo,
                    sensor,
                    sensorIndex,
                    key: `${deviceId}-${sensor.id || sensorIndex}`
                });
            });
        } else {
            // Device with no sensors
            sensorCards.push({
                ...deviceInfo,
                sensor: null,
                sensorIndex: 0,
                key: `${deviceId}-empty`
            });
        }
    });
    
    return sensorCards.map((card) => {
        const { deviceId, deviceName, deviceDescription, sensor, sensorIndex, key, id: deviceDbId, deviceMeta } = card;

        // Friendly label for the sensor card title. We want the physical
        // model code (e.g. "AIS01-LB") rather than the humanised sensor
        // type constant ("AI Meter Reader"). Order of preference:
        //   1. user-set sensor.name (if it's not just the DEV EUI)
        //   2. brand/model code from device meta (e.g. "AIS01-LB")
        //   3. model code mapped from sensor.part_number (set once a type
        //      is picked in admin)
        //   4. humanised part_number (last-resort, generic types we don't
        //      have a 1:1 model mapping for)
        //   5. "Sensor N" placeholder
        const getCardTitle = () => {
            if (sensor && sensor.name && !looksLikeDevEui(sensor.name)) {
                return sensor.name;
            }
            if (deviceMeta) {
                const label = getLoraDeviceLabel(deviceMeta);
                if (label && !looksLikeDevEui(label) && label !== 'Unknown LoRa Device') {
                    return label;
                }
            }
            if (sensor && sensor.part_number) {
                const modelCode = MODEL_CODE_BY_SENSOR_TYPE[sensor.part_number];
                if (modelCode) return modelCode;
                return humanizeSensorTypeName(sensor.part_number);
            }
            return `Sensor ${sensorIndex + 1}`;
        };
        
        if (!sensor) {
            // Render empty device card
            return (
                <Col xl={8} lg={12} md={12} sm={24} xs={24} key={key} style={{ marginBottom: '20px' }}>
                    <Card 
                        hoverable 
                        title={
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <div style={{ flex: 1 }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        {deviceName ? (
                                            <>
                                                <Title level={4} style={{ margin: 0, fontSize: '18px' }}>{deviceName}</Title>
                                                <EditOutlined 
                                                    onClick={(e) => { 
                                                        e.stopPropagation(); 
                                                        e.preventDefault(); 
                                                        handleEditDevice({ id: deviceDbId, device_name: deviceName, device_description: deviceDescription }, 'name', 'edit_device_name'); 
                                                    }} 
                                                    style={{ cursor: 'pointer', color: '#0B2A1B', fontSize: '14px' }} 
                                                    title="Edit device name" 
                                                />
                                            </>
                                        ) : (
                                            <Button 
                                                type="link" 
                                                size="small" 
                                                onClick={(e) => { 
                                                    e.stopPropagation(); 
                                                    e.preventDefault(); 
                                                    handleEditDevice({ id: deviceDbId }, 'name', 'edit_device_name'); 
                                                }} 
                                                style={{ fontSize: '12px', color: '#0B2A1B', padding: '0 4px', height: '20px', fontWeight: '500' }}
                                            >
                                                + Add Name
                                            </Button>
                                        )}
                                    </div>
                                    <Text type="secondary" style={{ fontSize: '12px' }}>{`ID: ${deviceId}`}</Text>
                                </div>
                            </div>
                        }
                    >
                        <div className="no-sensors-message">No sensors available for this device</div>
                    </Card>
                </Col>
            );
        }
        
        // Render sensor card
        const sensorTypes = getSensorTypes(sensor);
        const hasData = sensorTypes.length > 0;
        const isStorageTank = isConfiguredStorageTank(sensor);
        
        return (
            <Col xl={8} lg={12} md={12} sm={24} xs={24} key={key} style={{ marginBottom: '20px' }}>
                <Card 
                    hoverable={hasData}
                    className={hasData ? 'sensor-card-clickable' : ''}
                    onClick={() => hasData && handleSensorClick(sensor)}
                    title={
                        <div>
                            {/* Device Info */}
                            <div style={{ marginBottom: '8px', paddingBottom: '8px', borderBottom: '1px solid #f0f0f0' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    {deviceName ? (
                                        <>
                                            <Text type="secondary" style={{ fontSize: '12px' }}>{deviceName}</Text>
                                            <EditOutlined 
                                                onClick={(e) => { 
                                                    e.stopPropagation(); 
                                                    e.preventDefault(); 
                                                    handleEditDevice({ id: deviceDbId, device_name: deviceName, device_description: deviceDescription }, 'name', 'edit_device_name'); 
                                                }} 
                                                style={{ cursor: 'pointer', color: '#999', fontSize: '12px' }} 
                                                title="Edit device name" 
                                            />
                                        </>
                                    ) : (
                                        <Button 
                                            type="link" 
                                            size="small" 
                                            onClick={(e) => { 
                                                e.stopPropagation(); 
                                                e.preventDefault(); 
                                                handleEditDevice({ id: deviceDbId }, 'name', 'edit_device_name'); 
                                            }} 
                                            style={{ fontSize: '11px', color: '#999', padding: 0, height: '16px' }}
                                        >
                                            + Device Name
                                        </Button>
                                    )}
                                </div>
                                <Text type="secondary" style={{ fontSize: '11px' }}>{`Device ID: ${deviceId}`}</Text>
                            </div>
                            
                            {/* Sensor Info */}
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Text strong style={{ fontSize: '16px' }}>
                                    {getCardTitle()}
                                </Text>
                                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                    <Dropdown 
                                        overlay={renderSensorMenu(sensor)} 
                                        trigger={['click']}
                                        onClick={(e) => { 
                                            e.stopPropagation(); 
                                            e.preventDefault(); 
                                        }}
                                    >
                                        <PlusOutlined 
                                            style={{ 
                                                cursor: 'pointer', 
                                                color: '#0B2A1B', 
                                                fontSize: '14px', 
                                                zIndex: 10, 
                                                position: 'relative' 
                                            }} 
                                            title="Manage views" 
                                            onClick={(e) => { 
                                                e.stopPropagation(); 
                                                e.preventDefault(); 
                                            }} 
                                        />
                                    </Dropdown>
                                    <EditOutlined 
                                        onClick={(e) => { 
                                            e.stopPropagation(); 
                                            e.preventDefault(); 
                                            handleEditSensor(sensor, '', 'edit'); 
                                        }} 
                                        style={{ 
                                            cursor: 'pointer', 
                                            color: '#0B2A1B', 
                                            fontSize: '14px', 
                                            zIndex: 10, 
                                            position: 'relative' 
                                        }} 
                                        title="Edit sensor name" 
                                    />
                                </div>
                            </div>
                        </div>
                    }
                >
                    {sensor.asset && (
                        <div style={{ marginBottom: '12px' }}>
                            <Text type="secondary" style={{ fontSize: '12px' }}>
                                Asset: {sensor.asset.title}
                                {isStorageTank && (
                                    <span style={{ 
                                        marginLeft: '8px', 
                                        padding: '2px 8px', 
                                        backgroundColor: '#0d3825', 
                                        color: 'white', 
                                        borderRadius: '4px', 
                                        fontSize: '10px',
                                        fontWeight: 'bold'
                                    }}>
                                        STORAGE TANK
                                    </span>
                                )}
                            </Text>
                        </div>
                    )}
                    
                    {hasData ? (
                        <div>
                            {/* Render Storage Tank Display for configured storage tanks */}
                            {isStorageTank ? (
                                <div onClick={(e) => e.stopPropagation()}>
                                    <StorageTankDisplay sensor={sensor} asset={sensor.asset} />
                                    
                                    {/* Still show other sensor types below the tank display */}
                                    {sensorTypes.filter(type => type !== 'distance').map((sensorType) => {
                                        const lastValue = getLastKnownValue(sensor, sensorType);
                                        if (!lastValue) return null;
                                        const hasTypeSettings = hasSensorTypeSettings(sensor.id, sensorType);
                                        return (
                                            <div key={sensorType} className={`sensor-type-row ${sensorType}`}>
                                                {/* ... existing code ... */}
                                            </div>
                                        );
                                    })}
                                </div>
                            ) : sensor.part_number === 'ASSET_TRACKING' ? (
                                <div key="asset_tracking" className="sensor-type-row asset_tracking">
                                    <div className="sensor-type-content">{renderAssetTrackingMap(sensor)}</div>
                                </div>
                            ) : sensor.part_number === SENSOR_TYPE_AI_METER_READER ? (
                                /* AIS01-LB: only the meter reading is relevant on the card —
                                   battery/firmware/freq-band/sensor-model are hidden. */
                                <MeterReadingDisplay sensor={sensor} />
                            ) : (
                                <div>
                                    {sensorTypes.includes('temperature') && <TemperatureDisplay sensor={sensor} />}
                                    {sensorTypes.includes('humidity') && <HumidityDisplay sensor={sensor} />}
                                    {sensorTypes.includes('moisture') && <MoistureDisplay sensor={sensor} />}
                                    {sensorTypes.includes('pressure') && <PressureDisplay sensor={sensor} />}
                                    {sensorTypes.includes('voltage') && <VoltageDisplay sensor={sensor} />}
                                    {sensorTypes.includes('conductivity') && <ConductivityDisplay sensor={sensor} />}
                                    {sensorTypes.includes('relay') && <RelayDisplay sensor={sensor} />}
                                    {sensorTypes.includes('distance') && <DistanceDisplay sensor={sensor} />}
                                    {sensorTypes.includes('height') && <HeightDisplay sensor={sensor} />}
                                    {sensorTypes.includes('wind_speed') && <WindSpeedDisplay sensor={sensor} />}
                                    {sensorTypes.includes('wind_direction') && <WindDirectionDisplay sensor={sensor} />}
                                    {sensorTypes.includes('vent_position') && <VentPositionDisplay sensor={sensor} />}
                                    {sensorTypes.includes('light') && <LightDisplay sensor={sensor} />}
                                    {sensorTypes.includes('door_status') && (
                                        sensor.part_number === SENSOR_TYPE_DOOR_CONTACT_SENSOR_WATER
                                            ? <WaterMeterDisplay sensor={sensor} timezone={selectedTimezone} />
                                            : <DoorStatusDisplay sensor={sensor} timezone={selectedTimezone} />
                                    )}

                                    {/* Generic gauge for all other sensor types */}
                                    {sensorTypes.filter(type =>
                                        !['temperature', 'humidity', 'moisture', 'pressure', 'voltage', 'conductivity', 'relay', 'distance', 'height', 'wind_speed', 'wind_direction', 'vent_position', 'light', 'door_status', 'total_pulses', 'last_duration'].includes(type)
                                    ).map((sensorType) => {
                                        const lastValue = getLastKnownValue(sensor, sensorType);
                                        if (!lastValue) return null;
                                        const hasTypeSettings = hasSensorTypeSettings(sensor.id, sensorType);
                                        
                                        // Determine appropriate min/max values based on sensor type
                                        let minValue = 0;
                                        let maxValue = 100;
                                        let label = getSensorTypeLabel(sensorType);
                                        
                                        // Customize ranges for known sensor types
                                        switch(sensorType.toLowerCase()) {
                                            case 'air_quality':
                                            case 'co2':
                                                maxValue = 5000;
                                                break;
                                            case 'light':
                                            case 'lux':
                                                maxValue = 10000;
                                                break;
                                            case 'uv':
                                                maxValue = 15;
                                                break;
                                            case 'pm2.5':
                                            case 'pm10':
                                                maxValue = 500;
                                                break;
                                            case 'vibration':
                                                maxValue = 10;
                                                break;
                                            case 'noise':
                                            case 'sound':
                                                maxValue = 120;
                                                break;
                                            default:
                                                // Try to infer from actual value
                                                const value = parseFloat(lastValue.value);
                                                if (value > 100) {
                                                    maxValue = Math.ceil(value * 1.5);
                                                }
                                        }
                                        
                                        return (
                                            <div key={sensorType} style={{ position: 'relative' }}>
                                                {hasTypeSettings && (
                                                    <BellOutlined 
                                                        style={{ 
                                                            position: 'absolute',
                                                            top: '20px',
                                                            right: '20px',
                                                            color: '#0B2A1B', 
                                                            fontSize: '14px',
                                                            zIndex: 1
                                                        }} 
                                                        title={`Settings configured for ${sensorType}`} 
                                                    />
                                                )}
                                                <GenericGaugeDisplay 
                                                    sensor={sensor}
                                                    sensorType={sensorType}
                                                    label={label}
                                                    minValue={minValue}
                                                    maxValue={maxValue}
                                                />
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                            <div className="click-hint">
                                <Text type="secondary">Click to view details</Text>
                            </div>
                        </div>
                    ) : (
                        <div className="no-data-message">No recent data available</div>
                    )}
                </Card>
            </Col>
        );
    });
}, [devices, sensorSettings, sensorViews, selectedViewId]);

    const handleEditDevice = (device, field, typeModal) => {
        setEditDevice(device);
        setEditDeviceField(field);
        setTypeModalSensor(typeModal);
        setEditSensorNameModalVisible(true);
    };

    const getDevices = (property_id, start_date, end_date, timezone, searchTerm) => {
        setLoading(true);
        setDevices([]); // Clear devices immediately when starting to load
        const params = { start_date, end_date, property_id, timezone, page: currentPage, pageSize: pageSize, searchTerm };
        deviceSensorService.getList(params).then((res) => {
            if (res.code === "0000") {
                setDevices(res.devices);
                setTotalItems(res.total_item || 0);
                setTotalPages(res.total_page || 0);
                if (res.current_page) {
                    setCurrentPage(parseInt(res.current_page));
                }
            } else {
                // If response code is not success, ensure devices is empty
                setDevices([]);
                setTotalItems(0);
                setTotalPages(0);
            }
            setLoading(false);
        }).catch((error) => {
            console.error("Error fetching devices:", error);
            setDevices([]); // Clear devices on error
            setTotalItems(0);
            setTotalPages(0);
            setLoading(false);
        });
    }

    const fetchViewDataForTV = async (viewId) => {
        try {
            const res = await sensorViewService.getDetail(viewId, {
                start_date: startDate,
                end_date: endDate,
                timezone: selectedTimezone,
                is_show_sensors: true
            });
            
            if (res && res.code === '0000') {
                return {
                    devices: res.devices || [],
                    total_item: res.total_item || 0
                };
            }
            return { devices: [], total_item: 0 };
        } catch (error) {
            console.error('Error fetching view data for TV mode:', error);
            return { devices: [], total_item: 0 };
        }
    };

    const fetchDevicesForTV = async ({ page, pageSize, startDate, endDate, timezone }) => {
        const params = { start_date: startDate, end_date: endDate, property_id: activePropertyId, timezone, page, pageSize, searchTerm: '' };
        try {
            const res = await deviceSensorService.getList(params);
            if (res.code === "0000") {
                return { devices: res.devices || [], total_item: res.total_item || 0, total_page: res.total_page || 0 };
            }
            return { devices: [], total_item: 0, total_page: 0 };
        } catch (error) {
            console.error("Error fetching devices for TV mode:", error);
            return { devices: [], total_item: 0, total_page: 0 };
        }
    };

    const handleEditSensor = (sensor, sensorType, typeModal) => {
        setEditSensor(sensor);
        setStatsSensor(typeModal === 'alarm' ? sensor?.stats[sensorType] : {});
        setSensorType(sensorType);
        setTypeModalSensor(typeModal);
        setEditSensorNameModalVisible(true);
    };
    
    const handleSearch = useCallback(
        debounce((value) => {
            setSearchTerm(value);
            setCurrentPage(1);
            getDevices(activePropertyId, startDate, endDate, selectedTimezone, value);
        }, 500),
        [activePropertyId, startDate, endDate, selectedTimezone]
    );

    const handleClearSearch = () => {
        setSearchTerm('');
        setSearchValue('');
        setCurrentPage(1);
        getDevices(activePropertyId, startDate, endDate, selectedTimezone);
    };

    const cancelEditSensorNameModal = () => {
        setEditSensorNameModalVisible(false);
    }

    const onEditSensorNameSaved = (value) => {
        if (typeModalSensor === 'edit_device_name' || typeModalSensor === 'edit_device_description') {
            const fieldName = typeModalSensor === 'edit_device_name' ? 'device_name' : 'device_description';
            const params = { device: { [fieldName]: value } };
            deviceSensorService.updateDevice(params, editDevice.id).then(res => {
                if (res.code === '0000') {
                    setDevices(prevDevices => 
                        prevDevices.map(device => 
                            device.id === editDevice.id ? { ...device, [fieldName]: value } : device 
                        )
                    );
                    setEditSensorNameModalVisible(false);
                }
            });
        } else {
            const params = { sensor: { name: value } };
            sensorService.storeOrUpdate(params, editSensor.id).then(res => {
                if (res.code === '0000') {
                    setDevices(prevDevices => 
                        prevDevices.map(device => ({
                            ...device,
                            sensors: device.sensors.map(sensor => 
                                sensor.id === editSensor.id ? { ...sensor, name: value } : sensor 
                            )
                        }))
                    );
                    setEditSensorNameModalVisible(false);
                    if (selectedSensor && selectedSensor.id === editSensor.id) {
                        setSelectedSensor(prev => ({ ...prev, name: value }));
                    }
                }
            });
        }
    }

    if (currentView === 'tv') {
        const currentViewName = selectedViewId 
            ? sensorViews.find(v => v.id === selectedViewId)?.name 
            : null;
        
        return (
            <TVDisplayMode
                onExit={() => setCurrentView('list')}
                devices={devices}
                fetchDevices={fetchDevicesForTV}
                fetchViewData={selectedViewId ? fetchViewDataForTV : null}
                timezone={selectedTimezone}
                startDate={startDate}
                endDate={endDate}
                selectedViewId={selectedViewId}
                currentViewName={currentViewName}
            />
        );
    }
    if (currentView === 'detail' && selectedSensor) {
        return (
            <SensorDetail 
                sensor={selectedSensor}
                onBack={handleBackToList}
                timezone={selectedTimezone}
            />
        );
    }

    return (
        <LayoutWrapper>
            <SensorsPage>
                <Box id="containerBox">
                    <div className="manage-property-header">
                        <div className="mp-title">
                            <h2 className="manage-property-title"><IntlMessages id="sensorPage.title" /></h2>
                        </div>
                        
                    <Select
                        value={selectedViewId}
                        onChange={(value) => {
                            setDevices([]); // Clear devices immediately
                            setSelectedViewId(value);
                            setCurrentPage(1);
                        }}
                        style={{ width: 200, marginLeft: 'auto', marginRight: '10px' }}
                        size="large"
                        loading={viewsLoading}
                    >
                        <Option value={null}>
                            <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                All Sensors
                            </span>
                        </Option>
                        {sensorViews.map(view => (
                            <Option key={view.id} value={view.id}>
                                <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    {view.is_default === 1 && <StarFilled style={{ color: '#faad14' }} />}
                                    {view.name} ({view.sensor_count || 0})
                                </span>
                            </Option>
                        ))}
                    </Select>

                    <Button
                        type="default"
                        icon={<PlusOutlined />}
                        onClick={() => setCreateViewModalVisible(true)}
                        size="large"
                        style={{ marginRight: '10px' }}
                        className="custom-green-button"
                    >
                        New View
                    </Button>

                    {canAddDevice && hasLoraLink && (
                        <Button
                            type="primary"
                            icon={<PlusOutlined />}
                            onClick={() => setVisibleAddDevice(true)}
                            size="large"
                            style={{ marginRight: '10px' }}
                            className="custom-green-button"
                        >
                            Add Device
                        </Button>
                    )}

                    <AddDeviceModal
                        visible={visibleAddDevice}
                        onCancel={() => setVisibleAddDevice(false)}
                        propertyId={activePropertyId}
                        preloadedDevices={loraDevices}
                        onSuccess={() => {
                            if (selectedViewId) {
                                loadViewData(selectedViewId);
                            } else {
                                getDevices(activePropertyId, startDate, endDate, selectedTimezone);
                            }
                        }}
                    />

            {selectedViewId && (
                <>
                    <Button
                        type="default"
                        danger
                        icon={<DeleteOutlined />}
                        onClick={() => handleDeleteView(selectedViewId)}
                        size="large"
                        style={{ marginRight: '10px' }}
                        title="Delete this view"
                    >
                    </Button>
                </>
            )}
                        
                        <Button
                            type="primary"
                            icon={<TvIcon />}
                            onClick={() => setCurrentView('tv')}
                            size="large"
                            style={{ 
                                background: '#0d3825',
                                borderColor: '#0d3825',
                                marginRight: '10px'
                            }}
                        >
                        </Button>
                        
                        {!selectedViewId && (
                            <InputSearch
                                className="custom-search"
                                placeholder="Search devices, sensors, or assets..."
                                allowClear
                                value={searchValue}
                                size="large"
                                style={{ 
                                    width: 300, 
                                    display: 'flex', 
                                    borderRadius: 20
                                }}
                                onSearch={handleSearch}
                                onClear={handleClearSearch}
                                onChange={(e) => {
                                    const value = e.target.value;
                                    setSearchValue(value);
                                    handleSearch(value);
                                }}
                            />
                        )}
                    </div>

                    <div style={{padding: '15px'}}>
                        <Spin spinning={loading}>
                            <>
                                <Row gutter={[16, 16]}>
                                    {renderDeviceCards()}
                                </Row>
                                
                                {!selectedViewId && totalItems > 0 && (
                                    <div style={{ padding: "16px", textAlign: "center" }}>
                                        <Pagination
                                            current={currentPage}
                                            total={totalItems}
                                            pageSize={pageSize}
                                            onChange={handlePageChange}
                                            onShowSizeChange={handlePageSizeChange}
                                            showSizeChanger
                                            pageSizeOptions={['10', '20', '50', '100']}
                                            showQuickJumper
                                            showTotal={(total, range) =>
                                                `${range[0]}-${range[1]} of ${total} devices`
                                            }
                                        />
                                    </div>
                                )}
                                
                                {!loading && devices.length === 0 && (
                                    <div style={{ textAlign: 'center', padding: '40px' }}>
                                        <Text type="secondary" style={{ fontSize: '16px' }}>
                                            {selectedViewId 
                                                ? 'No sensors in this view. Add sensors using the eye icon on each sensor card.'
                                                : 'No devices found for this property'
                                            }
                                        </Text>
                                    </div>
                                )}
                            </>
                        </Spin>
                    </div>
                </Box>
            </SensorsPage>
            
            <Modal
                title="Create New View"
                visible={createViewModalVisible}
                onOk={handleCreateView}
                onCancel={() => {
                    setCreateViewModalVisible(false);
                    setNewViewName('');
                    setNewViewDescription('');
                }}
                okText="Create"
                okButtonProps={{
                    style: { backgroundColor: '#0B2A1B', borderColor: '#0B2A1B'}
                }}
            >
                <div style={{ marginBottom: '16px' }}>
                    <label style={{ display: 'block', marginBottom: '8px' }}>View Name *</label>
                    <Input
                        placeholder="Enter view name"
                        value={newViewName}
                        onChange={(e) => setNewViewName(e.target.value)}
                    />
                </div>
                <div>
                    <label style={{ display: 'block', marginBottom: '8px' }}>Description</label>
                    <Input.TextArea
                        placeholder="Enter description (optional)"
                        value={newViewDescription}
                        onChange={(e) => setNewViewDescription(e.target.value)}
                        rows={3}
                    />
                </div>
            </Modal>

            <EditSensorNameModal
                visible={editSensorNameModalVisible}
                sensor={editSensor}
                device={editDevice}
                deviceField={editDeviceField}
                statsSensor={statsSensor}
                usersProperty={usersProperty}
                sensorType={sensorType}
                typeModalSensor={typeModalSensor}
                onEditSensorNameSaved={onEditSensorNameSaved}
                onCancel={cancelEditSensorNameModal}
            />
        </LayoutWrapper>
    );
}

export default Sensors;